import React, { useState } from 'react';
import { Badge, Box, Drawer, Fab, Toolbar, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot } from '@fortawesome/pro-solid-svg-icons';
import { ProjectInput } from 'apiClient/Api';
import { AiAssistantPanel } from './AiAssistantPanel';

interface AiAssistantDrawerProps {
    /** Name des Gesprächs — meist der Kundenname aus dem Kalkulator. */
    name: string;
    /** Übernimmt den erarbeiteten Stand in die normale Kalkulator-Eingabe. */
    onApply: (project: ProjectInput, notes?: string | null) => void;
    /** Der Seiteninhalt. Er wird beim Öffnen zur Seite geschoben, nicht überdeckt. */
    children: React.ReactNode;
}

/** Breite auf dem Desktop. Schmal genug, dass der Kalkulator daneben bedienbar bleibt. */
const WIDTH = 420;

/**
 * Der Assistent als ausfahrbarer Bereich am rechten Rand.
 *
 * **Nicht im Seitenfluss, sondern daneben.** Der Vertriebler arbeitet im Kalkulator weiter,
 * während der Assistent offen bleibt — ein Panel über dem Inhalt hätte die Anforderungsauswahl
 * nach unten geschoben, genau die, auf die sich der Dialog bezieht.
 *
 * Auf dem Desktop schiebt sich der Inhalt zur Seite (`persistent`), auf schmalen Geräten legt sich
 * der Bereich darüber (`temporary`) — dort ist für zwei Spalten kein Platz.
 */
export const AiAssistantDrawer: React.FC<AiAssistantDrawerProps> = ({ name, onApply, children }) => {
    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down('lg'));
    const [open, setOpen] = useState(false);

    /**
     * MUI verschiebt bei `persistent` nichts von allein — der Inhalt muss selbst Platz machen.
     * Genau das hat vorher gefehlt: der Bereich lag über der Seite statt daneben.
     */
    const shifted = open && !isNarrow;

    return (
        <>
            <Box
                sx={{
                    marginRight: shifted ? `${WIDTH}px` : 0,
                    transition: theme.transitions.create('margin', {
                        easing: shifted ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
                        duration: shifted
                            ? theme.transitions.duration.enteringScreen
                            : theme.transitions.duration.leavingScreen,
                    }),
                }}
            >
                {children}
            </Box>

            {/* Der Auslöser bleibt sichtbar, solange der Bereich zu ist. */}
            {!open && (
                <Tooltip title="Angebotsassistent öffnen" placement="left">
                    <Fab
                        color="primary"
                        onClick={() => setOpen(true)}
                        sx={{ position: 'fixed', right: 24, bottom: 24, zIndex: theme.zIndex.drawer - 1 }}
                    >
                        <Badge color="secondary" variant="dot" invisible>
                            <FontAwesomeIcon icon={faRobot} />
                        </Badge>
                    </Fab>
                </Tooltip>
            )}

            <Drawer
                anchor="right"
                open={open}
                variant={isNarrow ? 'temporary' : 'persistent'}
                onClose={() => setOpen(false)}
                slotProps={{ paper: { sx: {
                    width: isNarrow ? '100%' : WIDTH, maxWidth: '100%',
                    display: 'flex', flexDirection: 'column',
                } } }}
            >
                {/*
                  * Kein zusätzlicher Rahmen und kein zweiter Scroll-Bereich: das Panel bringt seine
                  * eigene Aufteilung mit (Kopf, Verlauf, Eingabe) und füllt die volle Höhe. Zwei
                  * scrollende Ebenen waren der Grund, warum die Eingabe in der Mitte schwamm.
                  */}
                {/*
                  * Platzhalter in Höhe der Kopfleiste: die ist fixiert, und ohne diesen Abstand
                  * verschwindet der erste Gesprächsbeitrag darunter.
                  */}
                <Toolbar sx={{ flexShrink: 0 }} />

                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                    <AiAssistantPanel name={name} onApply={onApply} onClose={() => setOpen(false)} />
                </Box>
            </Drawer>
        </>
    );
};

export default AiAssistantDrawer;
